import { useState, useRef, useCallback } from "react";
import type {
  ChatMessage,
  StreamChunk,
} from "@shared-core/data/feature-session-types";
import { currentTime } from "./current-time";

interface UseStreamingChatOptions {
  mockMode?: boolean;
  repository?: string;
  branchName?: string;
}

interface UseStreamingChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  statusText: string | null;
  error: string | null;
  sendMessage: (text: string) => void;
  clearMessages: () => void;
}

const MOCK_RESPONSES = [
  "I've analysed the codebase and found a few areas to improve. Let me update the dashboard component to add the new chart layout you described.\n\n```tsx\nexport function DashboardChart({ data }) {\n  return (\n    <div className=\"grid grid-cols-2 gap-4\">\n      <LineChart data={data.revenue} />\n      <BarChart data={data.customers} />\n    </div>\n  );\n}\n```\n\nI've also updated the styles to match the design system. The changes are ready for preview.",
  "Good question. Let me look at the test coverage for that module.\n\nI found 3 test files that need updating. I'll run the existing tests first, then add the missing cases.\n\n```bash\n$ pnpm test -- --filter=dashboard\n  ✓ renders chart with data (12ms)\n  ✓ handles empty state (8ms)\n  ✗ updates on filter change\n```\n\nOne test is failing — the filter change handler wasn't wired up. I've fixed it and all tests pass now.",
  "I'll make that change. Updating the API route to include the new field and adjusting the frontend to display it.\n\nThe preview should update in a moment — check the right panel to see the result.",
  "Done. I've refactored the component to use the shared pattern from `components/patterns/`. The code is cleaner and consistent with the rest of the app.\n\nLet me know if you'd like any adjustments.",
];

function createMockId(): string {
  return `msg-${currentTime().getTime()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function getApiUrlAndToken(
  repository: string,
  branchName: string,
): Promise<{ url: string; headers: Record<string, string> }> {
  const { getApiBaseUrl, joinApiUrl, getAuthToken } =
    await import("@shared-core/data/api/client");

  const qs = new URLSearchParams({
    repository,
    branch_name: branchName,
  }).toString();
  const url = joinApiUrl(
    getApiBaseUrl(),
    `features/actions/send-chat-message?${qs}`,
  );

  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  return { url, headers };
}

export function useStreamingChat(
  options: UseStreamingChatOptions = {},
): UseStreamingChatReturn {
  const { mockMode = true, repository = "", branchName = "" } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const responseIndexRef = useRef(0);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setStatusText(null);
  }, []);

  const sendMessage = useCallback(
    (text: string) => {
      if (isStreaming) return;

      // Cancel any in-flight request
      abortRef.current?.abort();

      setError(null);

      // Add user message immediately
      const userMsg: ChatMessage = {
        id: createMockId(),
        role: "user",
        content: text,
        timestamp: currentTime().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      if (mockMode) {
        simulateMockStream();
      } else {
        streamFromApi(text);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendMessage identity is deliberately keyed on the listed inputs; the stream fns are hoisted decls recreated every render
    [isStreaming, mockMode, repository, branchName],
  );

  function simulateMockStream() {
    setIsStreaming(true);
    setStatusText("Thinking...");

    const fullText =
      MOCK_RESPONSES[responseIndexRef.current % MOCK_RESPONSES.length];
    responseIndexRef.current++;

    const assistantId = createMockId();
    let charIndex = 0;

    // Add empty assistant message
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: currentTime().toISOString(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, assistantMsg]);

    // Simulate status after a short delay
    const statusTimeout = setTimeout(() => {
      setStatusText("Writing code...");
    }, 400);

    // Stream characters
    const chunkSize = 3;
    const interval = setInterval(() => {
      charIndex += chunkSize;
      const content = fullText.slice(0, charIndex);
      const done = charIndex >= fullText.length;

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content, isStreaming: !done } : m,
        ),
      );

      if (done) {
        clearInterval(interval);
        clearTimeout(statusTimeout);
        setStatusText(null);
        setIsStreaming(false);
      }
    }, 20);
  }

  async function streamFromApi(text: string) {
    const controller = new AbortController();
    abortRef.current = controller;

    setIsStreaming(true);
    setStatusText("Connecting...");

    const assistantId = createMockId();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: currentTime().toISOString(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const { url, headers } = await getApiUrlAndToken(repository, branchName);

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: text }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      // response.body?.getReader() uses the Streams API (WHATWG ReadableStream).
      // Web: always supported. React Native: requires experimental_enableFetchStreaming
      // (opt-in, RN 0.73+). If unavailable, reader is undefined and the error below
      // fires immediately — no silent data loss.
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;

          const payload = trimmed.slice(6);
          if (payload === "[DONE]") continue;

          try {
            const chunk: StreamChunk = JSON.parse(payload);

            if (chunk.chunk_type === "text") {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: m.content + chunk.content }
                    : m,
                ),
              );
            } else if (chunk.chunk_type === "status") {
              setStatusText(chunk.content);
            } else if (chunk.chunk_type === "error") {
              setError(chunk.content);
            } else if (chunk.chunk_type === "result") {
              // Stream complete
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== "AbortError") {
        setError(err.message);
      }
    } finally {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, isStreaming: false } : m,
        ),
      );
      setStatusText(null);
      setIsStreaming(false);
    }
  }

  return {
    messages,
    isStreaming,
    statusText,
    error,
    sendMessage,
    clearMessages,
  };
}
